import React from 'react';
import classNames from 'classnames';
import { isUndefined } from 'lodash-es';

import { Pill } from './Pill';
import { SearchingPill } from './SearchingPill';
import { useTypewriter } from '../hooks/useTypewriter';
import { Highlight, HighlightGroup } from '../types';
import { wait } from '../utils';
import { useAppSelector } from '@/store';

const getText = (textObj: any, lang: string): string => {
  if (typeof textObj === 'string') return textObj;
  if (!textObj) return '';
  return textObj[lang] || textObj['en'] || Object.values(textObj)[0] || '';
};

interface AppleCardProps {
  data: Highlight;
  isActive: boolean;
  isCompleted: boolean;
  containerWidth: number;
  signal?: AbortSignal;
  onAnimationComplete: (cardId: string) => void;
  onClick: () => void;
}

export const AppleCard: React.FC<AppleCardProps> = (props) => {
  const { data: originalData, isActive, isCompleted, containerWidth, signal, onAnimationComplete, onClick } = props;

  const currentLanguage = useAppSelector((state: any) => state.application?.language || 'en');

  // Auto-append a SearchingPill to groups that don't already have one.
  // Only applies to multi-group cards (2+ groups). Single-group cards (flat tags) skip auto-append.
  const data = React.useMemo(() => {
    if (isUndefined(originalData.groups) || originalData.groups.length <= 1) {
      return originalData;
    }

    return {
      ...originalData,
      groups: originalData.groups.map((group) => {
        if (group.items.some((item) => item.isSearchingPill)) {
          return group;
        }

        return {
          ...group,
          items: [...group.items, { name: '', logo: '', isSearchingPill: true }],
        };
      }),
    };
  }, [originalData]);

  const headerRef = React.useRef<HTMLDivElement>(null);
  const cardRef = React.useRef<HTMLDivElement>(null);
  const innerContentRef = React.useRef<HTMLDivElement>(null);
  const [collapsedWidth, setCollapsedWidth] = React.useState<number>(120);

  const isActiveRef = React.useRef<boolean>(isActive);
  isActiveRef.current = isActive;

  // Typewriter hook
  const dataDescriptionText = getText(data.description, currentLanguage);
  const dataTitleText = getText(data.title, currentLanguage);

  // Measure the headline's natural (unconstrained) width by cloning it off-screen.
  // This runs once when the title text is known, so we always have the correct
  // collapsed target width for the CSS transition regardless of current card state.
  React.useEffect(() => {
    if (!headerRef.current) {
      return;
    }

    const probe = document.createElement('div');
    probe.className = headerRef.current.className;
    probe.style.cssText = [
      'position: fixed',
      'visibility: hidden',
      'pointer-events: none',
      'white-space: nowrap',
    ].join('; ');
    probe.innerHTML = headerRef.current.innerHTML;

    const parent = headerRef.current.parentElement!;
    parent.appendChild(probe);
    const naturalWidth = probe.offsetWidth + 20;
    parent.removeChild(probe);

    setCollapsedWidth(naturalWidth);
    // Re-measure if title text changes (language switch, etc.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataTitleText]);

  // Phase tracking for sequential pill animation
  const [animPhase, setAnimPhase] = React.useState<'idle' | 'typing' | 'pills' | 'done'>('idle');
  const [typingStarted, setTypingStarted] = React.useState<boolean>(false);

  // Track which pills have been "activated" (sequential reveal)
  const [activatedPillCount, setActivatedPillCount] = React.useState<number>(0);
  const totalPillsRef = React.useRef<number>(0);
  const pillCompletionCount = React.useRef<number>(0);

  // Typewriter hook
  const { displayedText, isComplete: typingComplete } = useTypewriter({
    text: dataDescriptionText,
    enabled: typingStarted,
    signal,
  });

  // Count total pills
  const totalPills = React.useMemo(() => {
    if (data.groups) {
      return data.groups.reduce((sum, group) => sum + group.items.length, 0);
    }

    return 0;
  }, [data]);

  // Consolidate ResizeObserver and height/transition management.
  // Matches v3 pattern: asymmetric transition delays and explicit height management.
  React.useEffect(() => {
    const cardEl = cardRef.current;
    const innerEl = innerContentRef.current;
    if (!cardEl || !innerEl) return;

    const MIN_EXPANDED_HEIGHT = 90;
    const VERTICAL_PADDING = 16;
    const APPLE_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

    // ResizeObserver watches the inner content and sets height ONLY when active
    const ro = new ResizeObserver(() => {
      if (isActiveRef.current && cardRef.current && innerContentRef.current) {
        cardRef.current.style.height = `${Math.max(innerContentRef.current.offsetHeight + VERTICAL_PADDING, MIN_EXPANDED_HEIGHT)}px`;
      }
    });

    ro.observe(innerEl);

    // Handle transition override and height reset when isActive changes
    if (isActive) {
      // Opening: override CSS to remove the height delay (0s)
      cardEl.style.transition = `border-radius 0.29s ease 0s, width 0.46s ${APPLE_EASE} 0s, height 0.46s ${APPLE_EASE} 0s, background-color 0.58s ease 0s`;
    } else {
      // Closing: restore CSS defaults (includes 0.29s height delay) and set explicit collapsed height
      cardEl.style.height = '40px';
      cardEl.style.transition = '';
    }

    return () => {
      ro.disconnect();
    };
  }, [isActive]); // Re-run when isActive changes to apply transition/height overrides

  // Start animation sequence when card becomes active
  React.useEffect(() => {
    if (!isActive) {
      setAnimPhase('idle');
      setTypingStarted(false);
      setActivatedPillCount(0);
      pillCompletionCount.current = 0;
      return;
    }

    if (isCompleted) {
      setAnimPhase('done');
      setTypingStarted(false);
      setActivatedPillCount(totalPills);
      return;
    }

    let cancelled = false;

    const abortHandler = () => {
      cancelled = true;
    };

    signal?.addEventListener('abort', abortHandler);

    const run = async () => {
      // Wait for card expansion animation
      await wait(805);

      if (cancelled) {
        return;
      }

      // Start typing
      setAnimPhase('typing');
      setTypingStarted(true);
    };

    run();

    return () => {
      cancelled = true;
      signal?.removeEventListener('abort', abortHandler);
    };
  }, [isActive, isCompleted, signal, totalPills]);

  // When typing completes, start pill sequence
  React.useEffect(() => {
    if (typingComplete && animPhase === 'typing') {
      if (totalPills === 0) {
        setAnimPhase('done');
        onAnimationComplete(data.id);
      } else {
        setAnimPhase('pills');
        totalPillsRef.current = totalPills;
        pillCompletionCount.current = 0;
        // Activate first pill
        setActivatedPillCount(1);
      }
    }
  }, [typingComplete, animPhase, totalPills, onAnimationComplete, data.id]);

  // Handle individual pill completion (advances to next pill)
  const handlePillComplete = React.useCallback(() => {
    pillCompletionCount.current += 1;

    if (pillCompletionCount.current >= totalPillsRef.current) {
      setAnimPhase('done');
      onAnimationComplete(data.id);
    } else {
      setActivatedPillCount((prev) => prev + 1);
    }
  }, [data.id, onAnimationComplete]);

  // Build the pill index map for groups
  // Single-group display rule: 1 group → hide title, 2+ groups → show all titles
  const renderGroupedPills = React.useCallback(
    (groups: HighlightGroup[]) => {
      let globalIndex = 0;
      const showGroupTitles = groups.length > 1;

      return groups.map((group, gIdx) => {
        const groupStartIndex = globalIndex;
        const showImmediate = isCompleted || animPhase === 'done';
        // Only show the group title when multiple groups AND at least one pill in this group is activated
        const showGroupTitle = showGroupTitles && (showImmediate || groupStartIndex < activatedPillCount);

        const groupTitleText = getText(group.title, currentLanguage);
        const pills = group.items.map((tag, tIdx) => {
          const myIndex = globalIndex++;
          const isActivated = myIndex < activatedPillCount;

          if (!isActivated && !showImmediate) return null;

          if (tag.isSearchingPill) {
            return (
              <SearchingPill
                key={`search-${gIdx}-${tIdx}`}
                immediate={showImmediate}
                signal={signal}
                onComplete={showImmediate ? undefined : handlePillComplete}
              />
            );
          }

          const tagNameText = getText(tag.name, currentLanguage);

          return (
            <Pill
              key={`pill-${gIdx}-${tIdx}`}
              text={tagNameText}
              logo={tag.logo}
              immediate={showImmediate}
              signal={signal}
              onComplete={showImmediate ? undefined : handlePillComplete}
            />
          );
        });

        return (
          <React.Fragment key={gIdx}>
            {showGroupTitle && <div className={'group-title'}>{groupTitleText}</div>}
            <div className={'group-items'}>{pills}</div>
          </React.Fragment>
        );
      });
    },
    [activatedPillCount, animPhase, isCompleted, signal, handlePillComplete, currentLanguage],
  );

  const showText = isCompleted || animPhase === 'done';
  const textToShow = showText ? dataDescriptionText : displayedText;

  return (
    <div
      ref={cardRef}
      className={classNames('highlight', { active: isActive })}
      style={{ width: isActive ? containerWidth : collapsedWidth }}
      onClick={onClick}
    >
      <div ref={innerContentRef} className={'highlight-wrapper'}>
        {/* Header row */}
        <div ref={headerRef} className={'headline'}>
          {/* Icon — hidden when active */}
          <i className={'bi bi-plus-circle-fill expand'}></i>
          <div className={'text'}>{dataTitleText}</div>
        </div>

        {/* Body — opacity/transform animated, height driven by ResizeObserver on parent */}
        <div className={'body'}>
          <div className={'text'}>{textToShow}</div>
          <div className={'dynamic-content'}>{data.groups ? renderGroupedPills(data.groups) : null}</div>
        </div>
      </div>
    </div>
  );
};
